import { Id } from "@iyk/prelude"
import * as UI from "@iyk/ui"
import type { MerchSku as CoreMerchSku } from "@iyk/vf-core"
import { Analytics, useAnalytics } from "@iyk/vf-observability/react"
import { Res } from "@iyk/vf-server"
import { Effect } from "effect"
import { ActivityEvent } from "../../activity/activity.ts"
import { useActivity } from "../../activity/use-activity.tsx"
import { Handler } from "../../server/handler.ts"
import type { Profile } from "../profile.ts"
import { BackToProfileButton } from "../ui/back-to-profile-button.tsx"
import { CheckIcon } from "../ui/icons.tsx"
import { MerchDetailSheet } from "../ui/merch-detail-sheet.tsx"
import type { Route } from "./+types/merch-detail.ts"

type LoaderData = {
  merchSku: Profile.Merch
  exclusives: Array<Profile.Exclusive>
}

export const loader = Handler.make<LoaderData>("profile-merch-detail", ({ api, params }) => {
  return Effect.gen(function* () {
    if (params.merchSkuId === undefined) {
      throw new Error("merchSkuId page param is required. Developer error!")
    }

    const merchSkuId = Id.make<CoreMerchSku>(params.merchSkuId)
    const merchSku = api.findMerch(merchSkuId)
    const exclusives = api.findMerchExclusives(merchSkuId)
    const data = yield* Effect.all({ merchSku, exclusives }, { concurrency: "unbounded" })

    return Res.json(data)
  }).pipe(Effect.catchTags({ MerchSkuNotFound: () => Effect.succeed(Res.notFound) }))
})

export default function ProfileMerchDetail(props: Route.ComponentProps) {
  const user = props.matches[1].data.user
  const userOwnsMerch = Boolean(user?.ownedMerchSkuIds.includes(props.loaderData.merchSku.id))
  const analytics = useAnalytics()
  const activity = useActivity()

  const trackBuyNowClick = async () => {
    analytics.track(Analytics.Event.MerchBuyNowClicked({ merchSkuId: props.loaderData.merchSku.id }))

    await activity.track(ActivityEvent.BuyLinkClicked({ merchSkuId: props.loaderData.merchSku.id }))
  }

  return (
    <div className="px-4">
      <BackToProfileButton />

      <div className="mt-8 px-7">
        <div className="block relative bg-[rgba(42,42,42,0.2)]">
          <img
            src={props.loaderData.merchSku.imageSrc}
            alt={props.loaderData.merchSku.title}
            className="size-full object-cover aspect-square"
          />
          {userOwnsMerch && (
            <div className="absolute top-2 right-2">
              <CheckIcon className="size-5" />
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 flex flex-col gap-1">
        <h3 className="text-xl">{props.loaderData.merchSku.name}</h3>
        <MerchDetailSheet merch={props.loaderData.merchSku} onBuyNowClick={trackBuyNowClick} />
      </div>

      {props.loaderData.merchSku.buyLink && (
        <div className="mt-6">
          <a
            onClick={trackBuyNowClick}
            className={UI.classForButton({ kind: "shaded", size: "lg", className: "w-full" })}
            href={props.loaderData.merchSku.buyLink}
            target="_blank"
          >
            Buy now <UI.Icon.ArrowUpRight />
          </a>
        </div>
      )}

      {/* TODO: implement exclusives */}
      {/* <div className="mt-14">
        <section className="flex flex-col gap-3">
          <p>Unlocks received by this item</p>
          <ExclusiveCardList owned={userOwnsMerch} exclusives={props.loaderData.exclusives} />
        </section>
      </div> */}
    </div>
  )
}
